import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Chip,
  Divider,
  CircularProgress,
  Alert
} from '@mui/material';
import { Business as BusinessIcon, Description as DocIcon } from '@mui/icons-material';
import { supabase } from '../services/supabaseClient';
import { Document } from '../types/documents';

interface QuickDocumentNavigationProps {
  onDocumentSelect: (documentId: number | string) => void;
  selectedDocId?: number | string | null;
  language?: 'English' | 'Hebrew';
}

export default function QuickDocumentNavigation({ 
  onDocumentSelect, 
  selectedDocId, 
  language = 'English' 
}: QuickDocumentNavigationProps) {
  const [companySpecificDocs, setCompanySpecificDocs] = useState<Document[]>([]);
  const [generalDocs, setGeneralDocs] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fontFamily = language === 'Hebrew' 
    ? 'Segoe UI, Tahoma, Geneva, Verdana, Arial, sans-serif, "Segoe UI Hebrew", "Noto Sans Hebrew"'
    : 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif';

  useEffect(() => {
    fetchRecentDocuments();
  }, []);

  const fetchRecentDocuments = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch documents from V3 schema
      const { data: documentsData, error: supabaseError } = await supabase
        .from('documents')
        .select(`
          id, title, filename, url, storage_url, doc_type,
          report_date, created_at, source,
          analyst_names, company_tickers
        `)
        .order('created_at', { ascending: false })
        .limit(10);

      if (supabaseError) {
        throw supabaseError;
      }

      if (documentsData && Array.isArray(documentsData)) {
        // Transform V3 documents to match Document interface
        const transformedDocs = documentsData.map((doc: any) => ({
          id: doc.id,
          title: doc.title || doc.filename,
          filename: doc.filename,
          sector_id: undefined,
          analyst_id: undefined,
          company_id: undefined,
          sector_name: '',
          analyst_name: doc.analyst_names?.join(', ') || '',
          company_name: '',
          company_ticker: doc.company_tickers?.[0] || '',
          chunk_count: 0,
          created_at: doc.created_at,
          timestamp: doc.report_date || doc.created_at,
          text_content: '',
          storage_url: doc.storage_url || doc.url,
          doc_type: doc.doc_type || 'PDF'
        }));

        // Separate company-specific and general documents (by company_tickers presence)
        const companyDocs = transformedDocs.filter(doc => doc.company_ticker);
        const generalDocs = transformedDocs.filter(doc => !doc.company_ticker);

        setCompanySpecificDocs(companyDocs.slice(0, 5));
        setGeneralDocs(generalDocs.slice(0, 5));
      }
    } catch (err: any) {
      console.error('Error fetching Evercore documents:', err);
      setError(err.message || 'Failed to fetch Evercore documents');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    // Format the date manually to avoid timezone conversion
    const year = date.getUTCFullYear();
    const month = date.toLocaleString(language === 'Hebrew' ? 'he-IL' : 'en-US', { month: 'short' });
    const day = date.getUTCDate();
    const hours = date.getUTCHours();
    const minutes = date.getUTCMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    
    return `${month} ${day}, ${year}, ${displayHours}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  };

  const getDocumentTitle = (doc: Document) => {
    if (doc.title) return doc.title;
    
    // Fallback title based on available data
    const parts = [];
    if (doc.company_name) parts.push(doc.company_name);
    if (doc.analyst_name) parts.push(doc.analyst_name);
    if (doc.sector_name) parts.push(doc.sector_name);
    
    return parts.length > 0 ? parts.join(' • ') : `Document ${doc.id}`;
  };

  const renderDocumentList = (documents: Document[], title: string, icon: React.ReactNode) => (
    <Box sx={{ mb: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        {icon}
        <Typography variant="subtitle2" sx={{ fontFamily, color: '#1A2341', fontWeight: 600, fontSize: '0.9rem' }}>
          {title}
        </Typography>
      </Box>
      
             {documents.length === 0 ? (
         <Typography sx={{ fontFamily, color: 'text.secondary', fontStyle: 'italic', fontSize: '0.8rem' }}>
           {language === 'Hebrew' ? 'אין מסמכים זמינים' : 'No documents available'}
         </Typography>
       ) : (
         <List sx={{ p: 0 }}>
           {documents.map((doc, index) => (
             <React.Fragment key={doc.id}>
               <ListItem sx={{ p: 0 }}>
                 <ListItemButton
                   onClick={() => onDocumentSelect(doc.id)}
                   selected={selectedDocId === doc.id}
                   sx={{
                     borderRadius: 1,
                     mb: 0.3,
                     py: 0.5,
                     '&.Mui-selected': {
                       bgcolor: '#E3F2FD',
                       '&:hover': {
                         bgcolor: '#BBDEFB',
                       },
                     },
                     '&:hover': {
                       bgcolor: '#F5F5F5',
                     },
                   }}
                 >
                  <ListItemText
                    primary={
                      <Typography sx={{ 
                        fontFamily, 
                        fontWeight: selectedDocId === doc.id ? 600 : 400,
                        fontSize: '0.8rem',
                        direction: language === 'Hebrew' ? 'rtl' : 'ltr',
                        textAlign: language === 'Hebrew' ? 'right' : 'left',
                        unicodeBidi: 'plaintext',
                        lineHeight: 1.3
                      }}>
                        {getDocumentTitle(doc)}
                      </Typography>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.3 }}>
                        <Typography variant="caption" sx={{ fontFamily, color: 'text.secondary', fontSize: '0.7rem' }}>
                          {doc.timestamp ? formatDate(doc.timestamp) : 'No date'}
                        </Typography>
                        {!!doc.chunk_count && (
                          <Chip 
                            label={`${doc.chunk_count} ${language === 'Hebrew' ? 'חלקים' : 'chunks'}`} 
                            size="small" 
                            variant="outlined"
                            sx={{ fontFamily, fontSize: '0.6rem' }}
                          />
                        )}
                      </Box>
                    }
                  />
                </ListItemButton>
              </ListItem>
              {index < documents.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      )}
    </Box>
  );

  if (loading) {
    return (
      <Paper sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: 2, bgcolor: '#fff' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={24} />
          <Typography sx={{ fontFamily }}>
            {language === 'Hebrew' ? 'טוען מסמכים אחרונים...' : 'Loading recent documents...'}
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: 2, bgcolor: '#fff' }}>
        <Alert severity="error" sx={{ fontFamily }}>
          {error}
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2, borderRadius: 3, boxShadow: 2, bgcolor: '#fff', height: 'fit-content' }}>
      <Typography variant="h6" sx={{ 
        fontFamily, 
        color: '#1A2341', 
        fontWeight: 700, 
        mb: 2,
        direction: language === 'Hebrew' ? 'rtl' : 'ltr',
        textAlign: language === 'Hebrew' ? 'right' : 'left',
        unicodeBidi: 'plaintext',
        fontSize: '1rem'
      }}>
        {language === 'Hebrew' ? 'מסמכים אחרונים' : 'Recent Documents'}
      </Typography>
      
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box>
          {renderDocumentList(
            generalDocs, 
            language === 'Hebrew' ? 'כללי' : 'General',
            <DocIcon sx={{ color: '#4A90E2', fontSize: '1.2rem' }} />
          )}
        </Box>
        
        <Box>
          {renderDocumentList(
            companySpecificDocs, 
            language === 'Hebrew' ? 'חברות' : 'Company',
            <BusinessIcon sx={{ color: '#2B3A67', fontSize: '1.2rem' }} />
          )}
        </Box>
      </Box>
    </Paper>
  );
} 